import { useGetAllShopEmailTemplates } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import { Button } from "@babackapp/ui/components/button";
import { useTranslation } from "react-i18next";
import { Position } from "@xyflow/react";
import { ExternalLink, Mail, TriangleAlert } from "lucide-react";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { useWorkflowsEditorRouting } from "../../context/workflows-editor-routing-context";
import { BaseHandle } from "../../components/base-handle";
import {
  BaseNode,
  BaseNodeContent,
  BaseNodeFooter,
} from "../../components/base-node";
import { ReturnEmailActionNodeProps } from "./types";
import { useNodeData } from "../../hooks/use-node-data";
import { ReturnEmailActionNodeDataDto } from "../node-types";
import { useIsWorkflowsListingMode } from "../../context/workflows-editor-workflows-context";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";

export const RETURN_EMAIL_NODE_WIDTH = 280;
export const RETURN_EMAIL_NODE_HEIGHT_ESTIMATION = 160;

export function ReturnEmailActionNodeEditor({
  id,
  data,
}: ReturnEmailActionNodeProps) {
  const { t } = useTranslation("trackoffice");
  const { data: shop } = useCurrentShopSuspense();
  const { openTemplate } = useWorkflowsEditorRouting();
  const { data: templates = [] } = useGetAllShopEmailTemplates(
    shop.id,
    "return",
  );
  const isWorkflowsListingMode = useIsWorkflowsListingMode();

  const liveData = useNodeData<ReturnEmailActionNodeDataDto>(id);
  const templateId =
    liveData?.actionType === "send_return_email"
      ? liveData.config.templateId
      : data.templateId;

  const selectedTemplate = templates?.find(
    (template) => template.id === templateId,
  );

  const handleCustomizeClick = () => {
    if (templateId) {
      openTemplate?.(templateId);
    }
  };

  return (
    <ContextualTipsTrigger
      tips={[
        {
          id: "workflow-node-email",
          Icon: Mail,
          title: t(
            "workflowsEditor.tips.workflowNodeEmail.title",
            "This is an email action node",
          ),
          description: t(
            "workflowsEditor.tips.workflowNodeEmail.desc",
            "This node will send an email to the customer using a specific template. You can customize the template using the button on the node's card.",
          ),
        },
      ]}
    >
      <BaseNode nodeId={id} style={{ position: "relative" }}>
        <BaseNodeContent>
          <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Template</span>
              {selectedTemplate ? (
                <span className="text-xs mt-1 font-semibold">
                  {selectedTemplate.title}
                </span>
              ) : (
                // No default-template fallback on the return workflow path:
                // a missing template fails the execution, so flag it here.
                <span className="text-xs mt-1 font-semibold text-amber-600 flex items-center gap-1">
                  <TriangleAlert className="size-3 shrink-0" />
                  {t(
                    "workflowsEditor.returnEmailNode.noTemplate",
                    "Select a template",
                  )}
                </span>
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Recipient</span>
              <span className="text-xs mt-1 font-semibold">
                {t("workflowsEditor.panels.emailRecipient.options.customer")}
              </span>
            </div>
          </div>
          <BaseHandle type="target" position={Position.Left} />
          <BaseHandle
            type="source"
            position={Position.Right}
            style={{ top: "50%", transform: "translateY(-50%)" }}
          />
        </BaseNodeContent>
        {selectedTemplate && !isWorkflowsListingMode && (
          <BaseNodeFooter>
            <div className="justify-end items-end w-full flex">
              <Button
                size="icon-xs"
                variant="ghost"
                className="nodrag shrink-0"
                onClick={handleCustomizeClick}
                title={t(
                  "workflowsEditor.emailNode.customizeTemplate",
                  "Customize template",
                )}
              >
                <ExternalLink className="size-3" />
              </Button>
            </div>
          </BaseNodeFooter>
        )}
      </BaseNode>
    </ContextualTipsTrigger>
  );
}
